// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useI18n } from '../i18n';
import { useSignupEnabled } from '../lib/useSignupEnabled';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.normascan.fr';

const Signup = () => {
  const [, setLocation] = useLocation();
  const { login } = useAuth();
  const { t } = useI18n();
  const signupEnabled = useSignupEnabled();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    cguAccepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.id]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('pwd_mismatch'));
      return;
    }
    if (!formData.cguAccepted) {
      setError(t('invite_cgu_required'));
      return;
    }

    setLoading(true);
    try {
      // ⚠️ `cgu_accepted` doit voyager : depuis le 29/07/2026 le serveur refuse
      // la création sans lui. La case ci-dessous n'est que l'expression du geste,
      // la serrure est côté serveur — un POST direct la contournerait.
      const response = await fetch(`${API_BASE_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          cgu_accepted: true,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la création du compte');
      }

      login(data.user);
      setLocation('/parametres?tab=billing');
    } catch (err) {
      setError(err.message || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  // Inscriptions fermées : on n'affiche pas un formulaire qui échouerait en 403.
  // Le serveur reste la seule serrure (`/api/register`), ceci n'est que de la
  // courtoisie envers le visiteur.
  if (signupEnabled === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('signup_closed_title')}</CardTitle>
            <CardDescription>{t('signup_closed_body')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Link href="/login">
              <Button className="w-full">{t('signup_closed_login')}</Button>
            </Link>
            <a href="https://normascan.fr/#contact" className="w-full">
              <Button variant="outline" className="w-full">{t('signup_closed_contact_cta')}</Button>
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link href="/">
            <span className="inline-flex items-center gap-2 mb-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl font-bold">N</span>
              </div>
              <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Normascan
              </span>
            </span>
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Créer un compte</CardTitle>
            <CardDescription>Commencez à automatiser vos analyses</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Nom complet</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Jean Dupont"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">{t('pwd_rules')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  id="cguAccepted"
                  type="checkbox"
                  checked={formData.cguAccepted}
                  onChange={handleChange}
                  className="mt-1"
                />
                <span>
                  {t('invite_cgu_prefix')}{' '}
                  <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {t('invite_cgu_terms')}
                  </a>{' '}
                  {t('invite_cgu_and')}{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {t('invite_cgu_privacy')}
                  </a>
                </span>
              </label>

              <Button type="submit" className="w-full" disabled={loading || !formData.cguAccepted}>
                {loading ? 'Création en cours...' : 'Créer mon compte'}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm">
              <p className="text-gray-600">
                Déjà un compte ?{' '}
                <Link href="/login">
                  <span className="text-blue-600 hover:underline font-medium">Se connecter</span>
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
